/**
 * FeaturedVideo Slice
 *
 * Large video showcase section with play button overlay.
 */

import React from "react";
import { graphql } from "gatsby";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import { GatsbyImage } from "gatsby-plugin-image";
import ReactPlayer from "react-player/lazy";
import Icon from "../../components/Icon";
import VisuallyHidden from "../../components/VisuallyHidden";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import * as styles from "./FeaturedVideo.module.scss";

export const FeaturedVideo = ({ slice }) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const { heading, video_url, video_poster, color_scheme, show_background_static_video } =
    slice?.primary || {};

  return (
    <section
      className={styles.container}
      data-color-scheme={color_scheme || "black"}
      data-background-static-video={show_background_static_video ? "true" : undefined}
      data-video-playing={isPlaying}
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      <div className={`page-width ${styles.content}`}>
        <motion.div
          className={styles.headingWrapper}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
        >
          {heading?.richText && (
            <PrismicRichText
              field={heading.richText}
              components={{
                heading2: ({ children }) => <h2 className="h0">{children}</h2>,
              }}
            />
          )}
        </motion.div>

        <motion.button
          className={styles.playButton}
          onClick={() => setIsPlaying(true)}
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Play video"
        >
          <Icon name="play" size="var(--icon-size, 12rem)" />
          <VisuallyHidden>Play video</VisuallyHidden>
        </motion.button>
      </div>

      <div className={styles.videoWrapper}>
        <div className={styles.videoWrapperInner}>
          {video_url && (
            <ReactPlayer
              url={video_url}
              width="100%"
              height="100%"
              playing={isPlaying}
              controls={true}
              onEnded={() => setIsPlaying(false)}
              onPause={() => setIsPlaying(false)}
              style={{ position: "absolute", top: 0, left: 0 }}
            />
          )}
          {video_poster?.gatsbyImageData && (
            <GatsbyImage
              image={video_poster.gatsbyImageData}
              alt=""
              className={styles.poster}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodyFeaturedVideo on PrismicHomepageDataBodyFeaturedVideo {
    primary {
      heading {
        richText
        text
      }
      video_url
      video_poster {
        url
        gatsbyImageData(
          width: 1920
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
      color_scheme
      show_background_static_video
    }
  }
`;
